import {
    MouseEvent as ReactMouseEvent,
    TouchEvent as ReactTouchEvent,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

interface IUseSliderDragParams {
    disabled: boolean,
    onOffset: (_offset: number, _width: number) => void,
}

export const useSliderDrag = ({disabled, onOffset}: IUseSliderDragParams) => {
    const [dragging, setDragging] = useState(false);
    const trackRef = useRef<HTMLDivElement | null>(null);

    const calculate = useCallback((clientX: number) => {
        const track = trackRef.current;
        if (!track) return;

        const rect = track.getBoundingClientRect();
        const offset = Math.max(0, Math.min(clientX - rect.left, rect.width));
        onOffset(offset, rect.width);
    }, [onOffset]);

    const startDrag = useCallback((clientX: number) => {
        if (disabled) return;
        setDragging(true);
        calculate(clientX);
        document.body.style.userSelect = 'none';
        document.body.style.touchAction = 'none';
    }, [disabled, calculate]);

    const handleMouseDown = useCallback(
        (e: ReactMouseEvent) => startDrag(e.clientX),
        [startDrag],
    );

    const handleTouchStart = useCallback(
        (e: ReactTouchEvent) => {
            if (e.touches.length) startDrag(e.touches[0].clientX);
        },
        [startDrag],
    );

    useEffect(() => {
        if (!dragging) return;

        const handleMouseMove = (e: MouseEvent) => {
            if (disabled) return;
            calculate(e.clientX);
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (disabled || !e.touches.length) return;
            calculate(e.touches[0].clientX);
        };

        const endDrag = () => {
            setDragging(false);
            document.body.style.userSelect = '';
            document.body.style.touchAction = '';
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', endDrag);
        window.addEventListener('touchmove', handleTouchMove, {passive: true});
        window.addEventListener('touchend', endDrag);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', endDrag);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', endDrag);
        };
    }, [dragging, disabled, calculate]);

    return {trackRef, dragging, handleMouseDown, handleTouchStart};
};

export default useSliderDrag;
